import axios from 'axios'
import { useAuthStore } from '@/store/authStore'
import { isPublicPath } from '@/utils/publicPaths'
import { getApiBaseUrl } from '@/config/env'

const API_BASE_URL = getApiBaseUrl()

const api = axios.create({
  baseURL: API_BASE_URL,
  // We authenticate via Bearer token, not cookies. Avoid credentialed CORS.
  withCredentials: false,
})

api.interceptors.request.use((config) => {
  const { token, tenantId } = useAuthStore.getState()
  
  // Ensure headers object exists
  config.headers = config.headers ?? {}
  
  // Add auth token if available
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  
  // Add refresh token if available
  const refreshToken = localStorage.getItem('refreshToken')
  if (refreshToken) {
    config.headers['x-refresh-token'] = refreshToken
  }
  
  // For all API requests except auth endpoints, include tenant ID
  const isAuthRequest = ['/auth/login', '/auth/signup', '/auth/refresh', '/auth/logout', '/auth/forgot-password', '/auth/reset-password'].some(
    path => config.url?.includes(path)
  )
  
  if (!isAuthRequest) {
    const explicitTenant = (config.headers['x-tenant-id'] || config.headers['X-Tenant-Id']) as string | undefined
    let effectiveTenantId = explicitTenant || tenantId

    if (!effectiveTenantId && token) {
      try {
        const parts = token.split('.')
        if (parts.length === 3 && parts[1]) {
          const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
          const jsonPayload = decodeURIComponent(
            atob(base64)
              .split('')
              .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
              .join('')
          )
          const parsed = JSON.parse(jsonPayload)
          if (parsed?.tenantId) {
            effectiveTenantId = parsed.tenantId
            useAuthStore.setState({ tenantId: parsed.tenantId })
          }
          if (!useAuthStore.getState().user && (parsed?.id || parsed?.email)) {
            useAuthStore.setState({
              user: {
                id: parsed.id || '',
                email: parsed.email || '',
                name: parsed.name || (parsed.email ? parsed.email.split('@')[0] : 'Usuario'),
                role: parsed.role || 'agent',
              },
            })
          }
        }
      } catch {
        // Ignorar error de decodificación
      }
    }

    if (effectiveTenantId) {
      config.headers['x-tenant-id'] = effectiveTenantId
    } else if (import.meta.env.DEV || import.meta.env.VITE_NODE_ENV === 'development') {
      // In development, use a default tenant ID if none is set
      config.headers['x-tenant-id'] = 'dev-tenant'
    } else {
      console.warn('No tenant ID available for request to', config.url)
    }
  }
  
  return config
})

let refreshPromise: Promise<string> | null = null

async function getNewAccessToken(): Promise<string> {
  if (refreshPromise) {
    return refreshPromise
  }

  refreshPromise = (async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken')
      if (!refreshToken) {
        throw new Error('No refresh token available')
      }

      const refreshUrl = API_BASE_URL ? `${API_BASE_URL}/auth/refresh` : '/auth/refresh'
      const response = await axios.post(refreshUrl, {
        refreshToken,
      })

      const accessToken: string | undefined =
        response.data?.tokens?.accessToken || response.data?.accessToken
      const newRefreshToken: string | undefined =
        response.data?.tokens?.refreshToken || response.data?.refreshToken

      if (!accessToken) {
        throw new Error('No access token returned from refresh endpoint')
      }

      // Update auth store with new token while preserving existing user and tenant data
      useAuthStore.getState().login({ token: accessToken })

      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken)
      }

      return accessToken
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

api.interceptors.response.use(
  (res) => {
    // Store new access token if provided in the response
    const newAccessToken = res.headers['x-access-token']
    if (newAccessToken) {
      const { login } = useAuthStore.getState()
      login({ token: newAccessToken })
    }
    
    // Store new refresh token if provided in the response
    const newRefreshToken = res.headers['x-refresh-token']
    if (newRefreshToken) {
      localStorage.setItem('refreshToken', newRefreshToken)
    }
    
    return res
  },
  async (error) => {
    const originalRequest = error?.config
    
    // If error is 401 and we haven't already tried to refresh the token
    const isAuthPath = originalRequest?.url?.includes('/auth/')
    if (error?.response?.status === 401 && originalRequest && !originalRequest._retry && !isAuthPath) {
      originalRequest._retry = true
      
      try {
        const newAccessToken = await getNewAccessToken()
        
        // Retry the original request with the new token
        originalRequest.headers = originalRequest.headers ?? {}
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        return api(originalRequest)
      } catch (refreshError) {
        // If refresh token is invalid or expired, log the user out
        localStorage.removeItem('refreshToken')
        useAuthStore.getState().logout()
        
        // Solo redirigir si no estamos ya en una página pública (login, legal, catálogo público, etc.)
        if (!isPublicPath(window.location.pathname)) {
          window.location.replace('/login')
        }
        return Promise.reject(refreshError)
      }
    }
    
    return Promise.reject(error)
  }
)

export default api
